/**
 * OfflineBanner - Displays when the user is offline
 *
 * Uses navigator.onLine and online/offline events to detect connectivity.
 * Shows a non-intrusive banner at the top of the page when offline.
 * Integrates with React Query to invalidate queries when coming back online.
 */
import { useState, useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { WifiOff, RefreshCw, Wifi } from 'lucide-react'
import clsx from 'clsx'
import './OfflineBanner.css'

interface OfflineBannerProps {
  /** Additional className */
  className?: string
  /** Position of the banner */
  position?: 'top' | 'bottom'
  /** Whether to show retry button */
  showRetry?: boolean
}

/**
 * Hook for detecting online/offline status with React Query integration
 */
export function useOnlineStatus(): { isOnline: boolean; wasOffline: boolean } {
  const queryClient = useQueryClient()
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )
  const [wasOffline, setWasOffline] = useState(false)

  const handleOnline = useCallback(() => {
    setIsOnline(true)
    setWasOffline(true)
    // Invalidate all queries when coming back online to refetch stale data
    queryClient.invalidateQueries()
    // Clear wasOffline after a brief delay to allow for "just reconnected" UI
    setTimeout(() => setWasOffline(false), 3000)
  }, [queryClient])

  const handleOffline = useCallback(() => {
    setIsOnline(false)
  }, [])

  useEffect(() => {
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [handleOnline, handleOffline])

  return { isOnline, wasOffline }
}

export function OfflineBanner({
  className,
  position = 'top',
  showRetry = true
}: OfflineBannerProps) {
  const { isOnline, wasOffline } = useOnlineStatus()
  const [isRetrying, setIsRetrying] = useState(false)

  const handleRetry = async () => {
    setIsRetrying(true)

    try {
      // Try to fetch a small resource to check connectivity
      await fetch('/api/health', { method: 'HEAD', cache: 'no-store' })
      // If successful, the online event should fire automatically
    } catch {
      // Still offline
    } finally {
      setIsRetrying(false)
    }
  }

  // Show "reconnected" message briefly when coming back online
  if (wasOffline && isOnline) {
    return (
      <div
        className={clsx(
          'offline-banner',
          `offline-banner--${position}`,
          'offline-banner--online',
          className
        )}
        role="alert"
        aria-live="polite"
      >
        <div className="offline-banner-content">
          <Wifi size={16} className="offline-banner-icon online" />
          <span>Back online. Refreshing data...</span>
        </div>
      </div>
    )
  }

  // Don't render anything if online
  if (isOnline) return null

  // Show offline banner
  return (
    <div
      className={clsx(
        'offline-banner',
        `offline-banner--${position}`,
        className
      )}
      role="alert"
      aria-live="polite"
    >
      <div className="offline-banner-content">
        <WifiOff size={16} className="offline-banner-icon" />
        <span>You're offline. Showing cached data.</span>
        {showRetry && (
          <button
            className="offline-banner-retry"
            onClick={handleRetry}
            disabled={isRetrying}
          >
            <RefreshCw
              size={14}
              className={clsx(isRetrying && 'spinning')}
            />
            {isRetrying ? 'Checking...' : 'Retry'}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Compact offline indicator for use in headers/toolbars
 */
export function OfflineIndicator({ className }: { className?: string }) {
  const { isOnline } = useOnlineStatus()

  if (isOnline) return null

  return (
    <div className={clsx('offline-indicator', className)} title="You're offline">
      <WifiOff size={14} />
    </div>
  )
}

export default OfflineBanner
